import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, Surface, Btn, StatusTag, Field, Input, CurrencyInput } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { Copy, Check, PencilSimple, X, Trash, ArrowCounterClockwise, PaperPlaneTilt, Plus, Printer } from "@phosphor-icons/react";

export default function Settlements() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [list, setList] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [appSettings, setAppSettings] = useState({ avgas_per_liter: 2.5, mogas_per_liter: 1.8 });
  const [weights, setWeights] = useState({});
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteFL, setDeleteFL] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const newCreateForm = useCallback(() => ({
    aircraft_id: "", user_id: "", flight_date: today,
    departure: "", arrival: "",
    hobbs_start: "", hobbs_end: "",
    fuel_start: "", fuel_end: "",
    instructor_hours: 0, is_training: false, notes: "",
  }), [today]);

  const load = useCallback(() => Promise.all([
    api.get(filter ? `/settlements?aircraft_id=${filter}` : "/settlements").then((r) => setList(r.data)),
    api.get("/aircraft").then(async (r) => {
      setAircraft(r.data);
      const w = {};
      for (const a of r.data) {
        try {
          const wr = await api.get(`/aircraft/${a.id}/bucket-weights`);
          w[a.id] = wr.data.weights || {};
        } catch (e) {
          if (process.env.NODE_ENV === "development") console.warn(`bucket-weights failed for ${a.registration}`, e);
        }
      }
      setWeights(w);
    }),
    isOwner
      ? api.get("/participants").then((r) => setParticipants(r.data))
          .catch((e) => { if (process.env.NODE_ENV === "development") console.warn("participants fetch failed", e); })
      : Promise.resolve(),
    api.get("/app-settings").then((r) => setAppSettings(r.data || {}))
      .catch((e) => { if (process.env.NODE_ENV === "development") console.warn("app-settings fetch failed", e); }),
  ]), [filter, isOwner]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/settlements/${id}`, { status });
    load();
  };

  const doDelete = async () => {
    if (!deleting) return;
    await api.delete(`/settlements/${deleting.id}?delete_flight_log=${deleteFL ? "true" : "false"}`);
    setDeleting(null);
    setDeleteFL(false);
    load();
  };

  // Compute derived fuel preview for the edit modal
  const editAc = editing && aircraft.find((a) => a.id === editing.aircraft_id);
  const editFuelRate = editAc
    ? ((editAc.fuel_type || "").toUpperCase().includes("MOGAS")
        ? appSettings.mogas_per_liter
        : appSettings.avgas_per_liter) || 0
    : 0;
  const editBurn = editForm && editForm.fuel_start !== "" && editForm.fuel_end !== ""
    ? Math.max(0, (parseFloat(editForm.fuel_start) || 0) - (parseFloat(editForm.fuel_end) || 0))
    : null;
  const editFuelEur = editBurn !== null ? +(editBurn * editFuelRate).toFixed(2) : (editForm?.fuel_amount || 0);

  const editTotalEur = editForm
    ? (parseFloat(editForm.variable_amount) || 0) + (parseFloat(editForm.instructor_amount) || 0) + (editFuelEur || 0)
    : 0;

  const saveEdit = async () => {
    if (!editing) return;
    const payload = {
      variable_amount: parseFloat(editForm.variable_amount) || 0,
      instructor_amount: parseFloat(editForm.instructor_amount) || 0,
    };
    if (editForm.fuel_start !== "" && editForm.fuel_end !== "") {
      payload.fuel_start = parseFloat(editForm.fuel_start);
      payload.fuel_end = parseFloat(editForm.fuel_end);
    } else {
      payload.fuel_amount = parseFloat(editForm.fuel_amount) || 0;
    }
    setBusy(true);
    try {
      await api.patch(`/settlements/${editing.id}`, payload);
      setEditing(null); setEditForm(null);
      await load();
    } finally { setBusy(false); }
  };

  // Compute derived fuel preview for create modal
  const createAc = createForm && aircraft.find((a) => a.id === createForm.aircraft_id);
  const createFuelRate = createAc
    ? ((createAc.fuel_type || "").toUpperCase().includes("MOGAS")
        ? appSettings.mogas_per_liter
        : appSettings.avgas_per_liter) || 0
    : 0;
  const createBurn = createForm && createForm.fuel_start !== "" && createForm.fuel_end !== ""
    ? Math.max(0, (parseFloat(createForm.fuel_start) || 0) - (parseFloat(createForm.fuel_end) || 0))
    : null;
  const createFuelEur = createBurn !== null ? +(createBurn * createFuelRate).toFixed(2) : 0;
  const createTotalTime = createForm && createForm.hobbs_start !== "" && createForm.hobbs_end !== ""
    ? Math.max(0, +((parseFloat(createForm.hobbs_end) || 0) - (parseFloat(createForm.hobbs_start) || 0)).toFixed(2))
    : 0;
  const createVarEur = createAc && createTotalTime
    ? +(createTotalTime * (createAc.cost_settings?.rate_variable_per_hour || 55)).toFixed(2)
    : 0;
  const createInstrEur = createAc && createForm?.instructor_hours
    ? +(parseFloat(createForm.instructor_hours) * (createAc.cost_settings?.instructor_per_hour || 100)).toFixed(2)
    : 0;
  const createTotalEur = +(createVarEur + createInstrEur + createFuelEur).toFixed(2);

  const createValid = useMemo(() => {
    if (!createForm) return false;
    return createForm.aircraft_id && createForm.user_id && createForm.flight_date
      && createForm.hobbs_start !== "" && createForm.hobbs_end !== ""
      && parseFloat(createForm.hobbs_end) > parseFloat(createForm.hobbs_start);
  }, [createForm]);

  const submitCreate = async () => {
    if (!createForm || !createValid) return;
    const payload = {
      aircraft_id: createForm.aircraft_id,
      user_id: createForm.user_id,
      flight_date: createForm.flight_date,
      departure: createForm.departure || "",
      arrival: createForm.arrival || "",
      hobbs_start: parseFloat(createForm.hobbs_start),
      hobbs_end: parseFloat(createForm.hobbs_end),
      instructor_hours: parseFloat(createForm.instructor_hours) || 0,
      is_training: !!createForm.is_training,
    };
    if (createForm.fuel_start !== "" && createForm.fuel_end !== "") {
      payload.fuel_start = parseFloat(createForm.fuel_start);
      payload.fuel_end = parseFloat(createForm.fuel_end);
    }
    setBusy(true);
    try {
      await api.post("/settlements", payload);
      setCreating(false); setCreateForm(null);
      await load();
    } finally { setBusy(false); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(txt);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div>
      <PageHeader overline="Finance" title="Settlements"
        subtitle="Cost contribution records — Revolut reference and the aircraft's bank account."
        actions={
          <div className="flex items-center gap-3 print:hidden">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono" data-testid="settlements-filter">
              <option value="">All aircraft</option>
              {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration}</option>)}
            </select>
            <Btn variant="ghost" onClick={() => window.print()} data-testid="print-settlements-btn">
              <Printer size={12} className="inline mr-1" />Print
            </Btn>
            {isOwner && (
              <Btn onClick={() => { setCreating(true); setCreateForm(newCreateForm()); }} data-testid="create-settlement-btn">
                <Plus size={12} className="inline mr-1" />New settlement
              </Btn>
            )}
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {list.length === 0 ? (
          <Surface className="p-12 text-center text-zinc-500 text-sm">No settlements yet — they auto-generate when a flight is logged.</Surface>
        ) : list.map((s) => (
          <Surface key={s.id} className="p-5" data-testid={`settlement-${s.id}`}>
            <div className="flex justify-between items-start gap-6 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">{fmtDate(s.flight_date)} · <span className="text-amber">{s.aircraft_registration}</span>{s.kind === "annual_fee" && <span className="ml-2 tag tag-amber" style={{padding:"1px 6px"}}>Annual fee</span>}</div>
                <div className="text-lg font-medium">{s.user_name}</div>
                <div className="text-xs text-zinc-500 mt-1 font-mono">
                  {s.kind === "annual_fee"
                    ? <>Annual fixed contribution · contract renewal</>
                    : <>Variable {fmtMoney(s.variable_amount)} · Instructor {fmtMoney(s.instructor_amount)} · Fuel {fmtMoney(s.fuel_amount)}</>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Cost contribution due</div>
                <div className="font-mono text-2xl text-amber">{fmtMoney(s.amount_amount)}</div>
              </div>
            </div>

            {(s.bank_account?.iban || s.bank_account?.account_holder) && (
              <div className="mt-4 pt-4 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono">
                <div>
                  <div className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] mb-1">Pay to · {s.aircraft_registration}</div>
                  {s.bank_account.account_holder && <div className="text-zinc-200">{s.bank_account.account_holder}</div>}
                  {s.bank_account.bank_name && <div className="text-zinc-400">{s.bank_account.bank_name}</div>}
                  {s.bank_account.iban && (
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-amber">{s.bank_account.iban}</code>
                      <button onClick={() => copy(s.bank_account.iban)} className="text-zinc-500 hover:text-amber"><Copy size={12} /></button>
                    </div>
                  )}
                </div>
                <div className="md:text-right">
                  <div className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] mb-1">Revolut reference</div>
                  <div className="flex items-center gap-2 md:justify-end">
                    <code className="bg-[#0A0A0A] border border-zinc-800 px-2 py-1 text-amber" data-testid={`ref-${s.id}`}>{s.reference}</code>
                    <button onClick={() => copy(s.reference)} className="text-zinc-500 hover:text-amber" data-testid={`copy-ref-${s.id}`}>
                      {copied === s.reference ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-end gap-3 flex-wrap">
              <StatusTag status={s.status} />
              {isOwner && s.status === "pending" && (
                <Btn variant="ghost" onClick={() => { setEditing(s); setEditForm({
                  variable_amount: s.variable_amount || 0,
                  instructor_amount: s.instructor_amount || 0,
                  fuel_amount: s.fuel_amount || 0,
                  fuel_start: s.fuel_start ?? "",
                  fuel_end: s.fuel_end ?? "",
                }); }} data-testid={`edit-settlement-${s.id}`}>
                  <PencilSimple size={12} className="inline mr-1" />Edit
                </Btn>
              )}
              {s.status === "pending" && s.user_id === user?.user_id && (
                <Btn variant="ghost" onClick={() => setStatus(s.id, "sent")} data-testid={`mark-sent-${s.id}`}>
                  <PaperPlaneTilt size={12} className="inline mr-1" />Mark as sent
                </Btn>
              )}
              {s.status === "sent" && (s.user_id === user?.user_id || isOwner) && (
                <Btn variant="ghost" onClick={() => setStatus(s.id, "pending")} data-testid={`unsend-${s.id}`}>
                  <ArrowCounterClockwise size={12} className="inline mr-1" />Unsend
                </Btn>
              )}
              {isOwner && s.status !== "confirmed" && (
                <Btn onClick={() => setStatus(s.id, "confirmed")} data-testid={`confirm-${s.id}`}>Confirm received</Btn>
              )}
              {isOwner && s.status === "confirmed" && (
                <Btn variant="ghost" onClick={() => setStatus(s.id, "sent")} data-testid={`unconfirm-${s.id}`}>
                  <ArrowCounterClockwise size={12} className="inline mr-1" />Revert
                </Btn>
              )}
              {isOwner && s.status !== "confirmed" && (
                <Btn variant="ghost" onClick={() => { setDeleting(s); setDeleteFL(false); }} data-testid={`delete-settlement-${s.id}`}>
                  <Trash size={12} className="inline mr-1" />Delete
                </Btn>
              )}
            </div>

            {(() => {
              const distrib = s.bucket_distribution;
              const w = (s.bucket_weights && Object.keys(s.bucket_weights).length) ? s.bucket_weights : (weights[s.aircraft_id] || {});
              const buckets = ["engine", "prop", "maintenance", "improvement"];
              const hasAny = distrib || Object.keys(w).length > 0;
              if (!hasAny) return null;
              return (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">
                    {distrib ? "Auto-allocated to reserves" : "Planned reserve allocation (weights from targets)"}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {buckets.map((b) => {
                      const pct = w[b] || 0;
                      const amt = distrib?.[b] ?? (s.amount_amount || 0) * pct;
                      return (
                        <div key={b} className="border border-zinc-800 bg-[#0A0A0A] p-3" data-testid={`distrib-${s.id}-${b}`}>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{b}</div>
                          <div className="font-mono text-amber text-lg">{fmtMoney(amt)}</div>
                          <div className="text-[10px] text-zinc-600 font-mono">{(pct * 100).toFixed(1)}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </Surface>
        ))}
      </div>

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="edit-settlement-modal">
          <Surface className="w-full max-w-xl p-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-medium">Edit settlement</h3>
                <div className="text-sm text-zinc-500 mt-1">{editing.user_name} · {editing.aircraft_registration} · {fmtDate(editing.flight_date)}</div>
              </div>
              <button onClick={() => { setEditing(null); setEditForm(null); }}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Variable EUR">
                <CurrencyInput value={editForm.variable_amount}
                  onChange={(v) => setEditForm({ ...editForm, variable_amount: v })}
                  data-testid="edit-variable" />
              </Field>
              <Field label="Instructor EUR">
                <CurrencyInput value={editForm.instructor_amount}
                  onChange={(v) => setEditForm({ ...editForm, instructor_amount: v })} />
              </Field>
              <Field label={`Fuel start (L) · ${editAc?.fuel_type || "AVGAS"} @ ${fmtNum(editFuelRate, 2)} €/L`}>
                <Input type="number" step="0.1" value={editForm.fuel_start}
                  placeholder="e.g. 100"
                  onChange={(e) => setEditForm({ ...editForm, fuel_start: e.target.value })}
                  data-testid="edit-fuel-start" />
              </Field>
              <Field label="Fuel end (L)">
                <Input type="number" step="0.1" value={editForm.fuel_end}
                  placeholder="e.g. 80"
                  onChange={(e) => setEditForm({ ...editForm, fuel_end: e.target.value })}
                  data-testid="edit-fuel-end" />
              </Field>
            </div>
            {editBurn !== null && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-mono border border-zinc-800 bg-[#0A0A0A] p-3" data-testid="edit-fuel-preview">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fuel burn</div>
                  <div className="text-zinc-200 text-base">{fmtNum(editBurn, 2)} L</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fuel cost</div>
                  <div className="text-amber text-base">{fmtMoney(editFuelEur)}</div>
                </div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-zinc-800 text-sm font-mono">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">New total</div>
                <div className="text-amber text-lg" data-testid="edit-total-eur">{fmtMoney(editTotalEur)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => { setEditing(null); setEditForm(null); }}>Cancel</Btn>
              <Btn onClick={saveEdit} disabled={busy} data-testid="edit-save-btn">{busy ? "Saving…" : "Save changes"}</Btn>
            </div>
          </Surface>
        </div>
      )}
      {deleting && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="delete-settlement-modal">
          <Surface className="w-full max-w-lg p-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-medium">Delete settlement</h3>
                <div className="text-sm text-zinc-500 mt-1">{deleting.user_name} · {deleting.aircraft_registration} · {fmtDate(deleting.flight_date)}</div>
              </div>
              <button onClick={() => { setDeleting(null); setDeleteFL(false); }}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              This will permanently remove the settlement <code className="text-amber font-mono">{deleting.reference}</code>.
            </div>
            {deleting.flight_log_id && (
              <label className="mt-5 flex items-start gap-3 border border-zinc-800 bg-[#0A0A0A] p-3 cursor-pointer" data-testid="delete-fl-toggle">
                <input
                  type="checkbox"
                  checked={deleteFL}
                  onChange={(e) => setDeleteFL(e.target.checked)}
                  className="mt-1 accent-amber"
                  data-testid="delete-fl-checkbox"
                />
                <div className="text-xs">
                  <div className="text-zinc-200">Also delete the linked flight log</div>
                  <div className="text-zinc-500 mt-1 font-mono">
                    Pilot total hours and aircraft membership hours will be decremented by the flight's total time. The booking (if any) will be reverted to its pre-log status. Hobbs counter on the aircraft is left as-is.
                  </div>
                </div>
              </label>
            )}
            {!deleting.flight_log_id && deleting.kind === "annual_fee" && (
              <div className="mt-5 text-xs text-zinc-500 font-mono">Annual-fee settlements have no linked flight log or booking — only the settlement record will be removed.</div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => { setDeleting(null); setDeleteFL(false); }} data-testid="delete-cancel-btn">Cancel</Btn>
              <Btn onClick={doDelete} data-testid="delete-confirm-btn">Delete settlement</Btn>
            </div>
          </Surface>
        </div>
      )}
      {creating && createForm && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="create-settlement-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-medium">New settlement</h3>
                <div className="text-sm text-zinc-500 mt-1 font-mono">Manually log a flight and auto-create its settlement.</div>
              </div>
              <button onClick={() => { setCreating(false); setCreateForm(null); }}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Aircraft">
                <select value={createForm.aircraft_id}
                  onChange={(e) => setCreateForm({ ...createForm, aircraft_id: e.target.value })}
                  className="h-10 w-full bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono"
                  data-testid="create-aircraft">
                  <option value="">— select —</option>
                  {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration} · {a.make_model}</option>)}
                </select>
              </Field>
              <Field label="Pilot">
                <select value={createForm.user_id}
                  onChange={(e) => setCreateForm({ ...createForm, user_id: e.target.value })}
                  className="h-10 w-full bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono"
                  data-testid="create-pilot">
                  <option value="">— select —</option>
                  {participants.map((p) => <option key={p.user_id} value={p.user_id}>{p.name} · {p.email}</option>)}
                </select>
              </Field>
              <Field label="Flight date">
                <Input type="date" value={createForm.flight_date}
                  onChange={(e) => setCreateForm({ ...createForm, flight_date: e.target.value })}
                  data-testid="create-date" />
              </Field>
              <Field label="Departure / Arrival">
                <div className="flex gap-2">
                  <Input value={createForm.departure} placeholder="LRBS"
                    onChange={(e) => setCreateForm({ ...createForm, departure: e.target.value.toUpperCase() })} />
                  <Input value={createForm.arrival} placeholder="LROP"
                    onChange={(e) => setCreateForm({ ...createForm, arrival: e.target.value.toUpperCase() })} />
                </div>
              </Field>
              <Field label="Hobbs start">
                <Input type="number" step="0.1" value={createForm.hobbs_start}
                  onChange={(e) => setCreateForm({ ...createForm, hobbs_start: e.target.value })}
                  data-testid="create-hobbs-start" />
              </Field>
              <Field label="Hobbs end">
                <Input type="number" step="0.1" value={createForm.hobbs_end}
                  onChange={(e) => setCreateForm({ ...createForm, hobbs_end: e.target.value })}
                  data-testid="create-hobbs-end" />
              </Field>
              <Field label={`Fuel start (L) · ${createAc?.fuel_type || "AVGAS"} @ ${fmtNum(createFuelRate, 2)} €/L`}>
                <Input type="number" step="0.1" value={createForm.fuel_start}
                  placeholder="optional"
                  onChange={(e) => setCreateForm({ ...createForm, fuel_start: e.target.value })}
                  data-testid="create-fuel-start" />
              </Field>
              <Field label="Fuel end (L)">
                <Input type="number" step="0.1" value={createForm.fuel_end}
                  placeholder="optional"
                  onChange={(e) => setCreateForm({ ...createForm, fuel_end: e.target.value })}
                  data-testid="create-fuel-end" />
              </Field>
              <Field label="Instructor hours">
                <Input type="number" step="0.1" value={createForm.instructor_hours}
                  onChange={(e) => setCreateForm({ ...createForm, instructor_hours: e.target.value })} />
              </Field>
              <Field label="Training flight">
                <label className="flex items-center gap-2 h-10">
                  <input type="checkbox" className="accent-amber"
                    checked={!!createForm.is_training}
                    onChange={(e) => setCreateForm({ ...createForm, is_training: e.target.checked })} />
                  <span className="text-xs text-zinc-400 font-mono">mark as training</span>
                </label>
              </Field>
            </div>
            <div className="mt-5 pt-4 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm font-mono" data-testid="create-preview">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total time</div>
                <div className="text-zinc-200">{fmtNum(createTotalTime, 2)} h</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Variable</div>
                <div className="text-zinc-200">{fmtMoney(createVarEur)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fuel ({createBurn !== null ? fmtNum(createBurn, 1) + " L" : "—"})</div>
                <div className="text-zinc-200">{fmtMoney(createFuelEur)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total EUR</div>
                <div className="text-amber text-base">{fmtMoney(createTotalEur)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => { setCreating(false); setCreateForm(null); }} data-testid="create-cancel-btn">Cancel</Btn>
              <Btn onClick={submitCreate} disabled={!createValid || busy} data-testid="create-submit-btn">{busy ? "Creating…" : "Create settlement"}</Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
